import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DocumentIcon,
  ShareIcon,
  GlobeAltIcon,
  LockOpenIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { microsoftAPI, googleAPI, oauthAPI } from '../../services/api';

const PROVIDERS = {
  MICROSOFT: 'microsoft',
  GOOGLE: 'google',
};

const SHARING_TYPES = {
  public: { label: 'Public', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', icon: GlobeAltIcon },
  anyone_with_link: { label: 'Anyone with Link', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400', icon: LockOpenIcon },
  external: { label: 'External Users', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400', icon: UsersIcon },
  internal: { label: 'Internal Only', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', icon: BuildingOfficeIcon },
};

const FILE_TYPE_ICONS = {
  document: '📄',
  spreadsheet: '📊',
  presentation: '📽️',
  pdf: '📕',
  image: '🖼️',
  video: '🎬',
  audio: '🎵',
  folder: '📁',
  archive: '🗜️',
  text: '📝',
  file: '📎',
};

export default function FileSharingPage() {
  const [activeProvider, setActiveProvider] = useState(null);
  const [files, setFiles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [connections, setConnections] = useState({ microsoft: false, google: false });

  // Filters
  const [sharingTypeFilter, setSharingTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

  useEffect(() => {
    checkConnections();
  }, []);

  const checkConnections = async () => {
    try {
      const response = await oauthAPI.getStatus();
      const msConnected = response.data.microsoft?.connected || false;
      const googleConnected = response.data.google?.connected || false;

      setConnections({ microsoft: msConnected, google: googleConnected });

      // Default to first connected provider
      if (msConnected) {
        setActiveProvider(PROVIDERS.MICROSOFT);
      } else if (googleConnected) {
        setActiveProvider(PROVIDERS.GOOGLE);
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to check connection status');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeProvider && connections[activeProvider]) {
      loadData(true);
    }
  }, [activeProvider, sharingTypeFilter, pagination.offset]);

  const loadData = async (autoSync = false) => {
    if (!activeProvider) return;

    try {
      setLoading(true);
      setError(null);
      setUpgradeRequired(false);

      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
      };

      if (sharingTypeFilter !== 'all') {
        params.sharingType = sharingTypeFilter;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      const api = activeProvider === PROVIDERS.MICROSOFT ? microsoftAPI : googleAPI;

      const [filesRes, summaryRes] = await Promise.all([
        api.getSharingFiles(params),
        api.getSharingSummary(),
      ]);

      const fetchedFiles = filesRes.data.data.files || [];
      setFiles(fetchedFiles);
      setPagination(prev => ({
        ...prev,
        total: filesRes.data.data.total || 0,
      }));
      setSummary(summaryRes.data.data);

      // Auto-trigger sync on first visit if no data has been synced yet
      if (autoSync && fetchedFiles.length === 0 && !summaryRes.data.data?.lastSyncedAt) {
        handleSync();
      }
    } catch (err) {
      const errorData = err.response?.data;

      if (errorData?.error === 'upgrade_required' || errorData?.code === 'PLAN_UPGRADE_REQUIRED') {
        setUpgradeRequired(true);
        setError(null);
      } else {
        setError(errorData?.message || errorData?.error || 'Failed to load file sharing data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!activeProvider) return;

    try {
      setSyncing(true);
      setError(null);

      const api = activeProvider === PROVIDERS.MICROSOFT ? microsoftAPI : googleAPI;
      await api.triggerSharingSync();

      await loadData();
    } catch (err) {
      const errorData = err.response?.data;
      setError(errorData?.message || errorData?.error || 'Failed to sync file sharing data');
    } finally {
      setSyncing(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, offset: 0 }));
    loadData();
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    while (bytes >= 1024 && i < units.length - 1) {
      bytes /= 1024;
      i++;
    }
    return `${bytes.toFixed(1)} ${units[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Upgrade required view
  if (upgradeRequired) {
    return (
      <div className="p-6">
        <Card className="text-center py-12">
          <ExclamationTriangleIcon className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Pro Plan Required
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
            File Sharing Audit is available on Pro plans and above.
            Upgrade to monitor external file sharing across your organization.
          </p>
          <Link to="/dashboard/billing">
            <Button variant="primary">
              Upgrade to Pro
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  // No providers connected view
  if (!loading && !connections.microsoft && !connections.google) {
    return (
      <div className="p-6">
        <Card className="text-center py-12">
          <ShareIcon className="w-16 h-16 mx-auto text-slate-400 mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Connect a Cloud Provider
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
            Connect Microsoft 365 or Google Workspace to audit file sharing
            and detect externally accessible files.
          </p>
          <Link to="/dashboard/connect">
            <Button variant="primary">
              Connect Provider
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            File Sharing Audit
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Monitor and manage externally shared files across your organization
          </p>
        </div>

        <Button
          variant="primary"
          onClick={handleSync}
          disabled={syncing || loading}
          className="flex items-center gap-2"
        >
          <ArrowPathIcon className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Files'}
        </Button>
      </div>

      {/* Provider Tabs */}
      <div className="flex gap-2">
        {connections.microsoft && (
          <button
            onClick={() => setActiveProvider(PROVIDERS.MICROSOFT)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeProvider === PROVIDERS.MICROSOFT
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            Microsoft OneDrive
          </button>
        )}
        {connections.google && (
          <button
            onClick={() => setActiveProvider(PROVIDERS.GOOGLE)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeProvider === PROVIDERS.GOOGLE
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            Google Drive
          </button>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <DocumentIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Total Shared</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.totalFiles}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <GlobeAltIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Public</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{summary.publicFiles}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <LockOpenIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Anyone with Link</p>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{summary.anyoneWithLink}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <UsersIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">External Users</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{summary.externalShared}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Risk Score</p>
                <p className={`text-2xl font-bold ${
                  summary.riskScore >= 70 ? 'text-red-600 dark:text-red-400' :
                  summary.riskScore >= 40 ? 'text-orange-600 dark:text-orange-400' :
                  summary.riskScore >= 20 ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-green-600 dark:text-green-400'
                }`}>{summary.riskScore}%</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by file name or owner..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </form>

          {/* Sharing Type Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-slate-400" />
            <select
              value={sharingTypeFilter}
              onChange={(e) => {
                setSharingTypeFilter(e.target.value);
                setPagination(prev => ({ ...prev, offset: 0 }));
              }}
              className="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Sharing Types</option>
              <option value="public">Public</option>
              <option value="anyone_with_link">Anyone with Link</option>
              <option value="external">External Users</option>
              <option value="internal">Internal Only</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Error Message */}
      {error && (
        <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </Card>
      )}

      {/* Files Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <LoadingSpinner size="md" text="Loading files..." />
          </div>
        ) : files.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentIcon className="w-12 h-12 mx-auto text-slate-400" />
            <h3 className="mt-2 text-lg font-medium text-slate-900 dark:text-white">No shared files found</h3>
            <p className="mt-1 text-slate-600 dark:text-slate-400">
              {summary?.syncStatus === 'idle' ? 'Click "Sync Files" to scan for shared files.' : 'No files match your current filters.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">File</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Owner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Sharing Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Size</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Modified</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {files.map((file) => {
                    const sharingInfo = SHARING_TYPES[file.sharingType] || SHARING_TYPES.internal;
                    const SharingIcon = sharingInfo.icon;

                    return (
                      <tr key={file.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{FILE_TYPE_ICONS[file.fileType] || FILE_TYPE_ICONS.file}</span>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white truncate max-w-xs" title={file.fileName}>
                                {file.fileName}
                              </p>
                              <p className="text-sm text-slate-500 dark:text-slate-400 capitalize">{file.fileType}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-slate-900 dark:text-white">{file.ownerName || '-'}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{file.ownerEmail}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sharingInfo.color}`}>
                            <SharingIcon className="w-3.5 h-3.5" />
                            {sharingInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-slate-600 dark:text-slate-400">
                          {formatFileSize(file.fileSize)}
                        </td>
                        <td className="px-4 py-4 text-slate-600 dark:text-slate-400">
                          {formatDate(file.modifiedAt)}
                        </td>
                        <td className="px-4 py-4">
                          {file.webUrl && (
                            <a
                              href={file.webUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                              Open
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} files
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <span className="px-3 py-1 text-slate-600 dark:text-slate-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Last Synced Info */}
      {summary && summary.lastSyncedAt && (
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
          Last synced: {new Date(summary.lastSyncedAt).toLocaleString('en-GB')}
          {summary.syncStatus === 'syncing' && ' (Sync in progress...)'}
        </p>
      )}
    </div>
  );
}
